import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useFamily } from '@/hooks/useFamily';

export interface FamilyDocument {
  id: string;
  family_id: string;
  title: string;
  description?: string;
  document_type: string;
  file_url?: string;
  content?: string;
  linked_persons?: string[];
  category: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDocumentData {
  title: string;
  description?: string;
  document_type: string;
  file_url?: string;
  content?: string;
  linked_persons?: string[];
  category: string;
}

// Fetch documents for a family
async function fetchDocuments(familyId: string): Promise<FamilyDocument[]> {
  const { data, error } = await supabase
    .from('family_documents')
    .select('*')
    .eq('family_id', familyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }

  return data || [];
}

export function useOptimizedDocuments(familyId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { profile } = useFamily();

  // Query for documents
  const {
    data: documents = [],
    isLoading: loading,
    error,
    refetch: refreshDocuments
  } = useQuery({
    queryKey: ['family-documents', familyId],
    queryFn: () => fetchDocuments(familyId!),
    enabled: !!familyId,
    staleTime: 0, // Force fresh data
    gcTime: 0, // Clear cache immediately
  });

  // Upload file function
  const uploadFile = async (file: File): Promise<string> => {
    if (!familyId) throw new Error('Family ID required');
    const fileExt = file.name.split('.').pop();
    const ownerFolder = (user?.id || 'unknown');
    const fileName = `${ownerFolder}/${Date.now()}.${fileExt}`;
    const filePath = `${familyId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('family-documents')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    // Return the storage key (private bucket)
    return filePath;
  };

  // Create document mutation
  const createDocumentMutation = useMutation({
    mutationFn: async (documentData: CreateDocumentData) => {
      if (!familyId || !profile) throw new Error('Family ID and profile required');

      const { data, error } = await supabase
        .from('family_documents')
        .insert({
          ...documentData,
          family_id: familyId,
          created_by: profile.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-documents', familyId] });
      toast({
        title: "Document ajouté",
        description: "Le document a été ajouté avec succès.",
      });
    },
    onError: (error) => {
      console.error('Error creating document:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter le document.",
      });
    },
  });

  // Update document mutation
  const updateDocumentMutation = useMutation({
    mutationFn: async ({ documentId, updates }: { documentId: string; updates: Partial<CreateDocumentData> }) => {
      const { data, error } = await supabase
        .from('family_documents')
        .update(updates)
        .eq('id', documentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-documents', familyId] });
      toast({
        title: "Document modifié",
        description: "Le document a été modifié avec succès.",
      });
    },
    onError: (error) => {
      console.error('Error updating document:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Impossible de modifier le document: ${error instanceof Error ? error.message : ''}`,
      });
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const { error } = await supabase
        .from('family_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      return documentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-documents', familyId] });
      toast({
        title: "Document supprimé",
        description: "Le document a été supprimé avec succès.",
      });
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le document.",
      });
    },
  });

  return {
    documents,
    loading,
    error,
    createDocument: createDocumentMutation.mutate,
    createDocumentAsync: createDocumentMutation.mutateAsync,
    updateDocument: (documentId: string, updates: Partial<CreateDocumentData>) =>
      updateDocumentMutation.mutate({ documentId, updates }),
    updateDocumentAsync: updateDocumentMutation.mutateAsync,
    deleteDocument: deleteDocumentMutation.mutate,
    deleteDocumentAsync: deleteDocumentMutation.mutateAsync,
    uploadFile,
    isCreating: createDocumentMutation.isPending,
    isUpdating: updateDocumentMutation.isPending,
    isDeleting: deleteDocumentMutation.isPending,
    isUploading: false, // We can add a separate upload mutation if needed
    // Legacy compatibility
    creating: createDocumentMutation.isPending,
    updating: updateDocumentMutation.isPending,
    uploading: false,
    refreshDocuments,
  };
}